import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MessageCircle, Users, ArrowLeft } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { OnlineIndicator } from '../components/OnlineIndicator';
import { getImageUrl } from '../utils/imageUtils';
import SubscriptionRequired from '../components/SubscriptionRequired';

export default function ChatsListPage() {
  const { user, api } = useAuth();
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [chatConversations, setChatConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSubscriptionPopup, setShowSubscriptionPopup] = useState(false);
  
  const canChat = user?.permissions?.can_chat === true;

  useEffect(() => {
    if (!canChat) {
      setShowSubscriptionPopup(true);
      return;
    }
    
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [canChat]);

  const fetchData = async () => {
    try {
      // Fetch online users
      const onlineRes = await api.get('/users/online');
      const onlineIds = onlineRes.data.online_user_ids || [];
      
      // Fetch profiles
      const usersRes = await api.get('/search');
      const allProfiles = Array.isArray(usersRes.data) ? usersRes.data : (usersRes.data.profiles || []);
      
      // Filter online users (excluding self)
      const online = allProfiles.filter(p => 
        onlineIds.includes(p.user_id) && p.user_id !== user.id
      );
      setOnlineUsers(online);
      
      // Fetch chat conversations
      const convRes = await api.get('/chat/conversations');
      setChatConversations(convRes.data || []);
      
    } catch (error) {
      console.error('Error fetching chat data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (showSubscriptionPopup) {
    return <SubscriptionRequired feature="chat" onClose={() => window.history.back()} />;
  }

  return (
    <div className="min-h-screen bg-dark pb-20">
      {/* Header */}
      <div className="sticky top-0 bg-dark-paper border-b border-white/10 z-10">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <Link to="/feed" className="p-2 text-gray-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-lg font-semibold text-white">Chat en direct</h1>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-6">
        {/* Online Users */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 text-green-500" />
            <h2 className="text-sm font-medium text-white">
              En ligne ({onlineUsers.length})
            </h2>
          </div>
          
          {loading ? (
            <div className="text-center py-4 text-gray-500">Chargement...</div>
          ) : onlineUsers.length === 0 ? (
            <div className="text-center py-4 text-gray-500">
              Aucun membre en ligne
            </div>
          ) : (
            <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
              {onlineUsers.map((profile) => (
                <Link
                  key={profile.user_id}
                  to={`/chat/${profile.user_id}`}
                  className="flex flex-col items-center gap-1 min-w-[72px]"
                >
                  <div className="relative">
                    <Avatar className="w-14 h-14 border-2 border-green-500">
                      <AvatarImage src={getImageUrl(profile.profile_picture)} />
                      <AvatarFallback className="bg-dark-subtle text-gold">
                        {profile.pseudo?.[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <OnlineIndicator isOnline={true} className="absolute bottom-0 right-0" />
                  </div>
                  <span className="text-xs text-white truncate max-w-[72px]">
                    {profile.pseudo}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Chat Conversations with unread */}
        {chatConversations.filter(c => c.unread_count > 0).length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <MessageCircle className="w-4 h-4 text-red-500" />
              <h2 className="text-sm font-medium text-white">
                Messages non lus
              </h2>
            </div>
            
            <div className="space-y-2">
              {chatConversations
                .filter(c => c.unread_count > 0)
                .map((conv) => (
                  <Link
                    key={conv.partner?.id}
                    to={`/chat/${conv.partner?.id}`}
                    className="flex items-center gap-3 p-3 bg-dark-subtle rounded-lg border border-red-500/30 hover:bg-dark-paper transition-colors"
                  >
                    <div className="relative">
                      <Avatar className="w-12 h-12">
                        <AvatarImage src={getImageUrl(conv.partner?.profile_picture || conv.profile?.profile_picture)} />
                        <AvatarFallback className="bg-dark text-gold">
                          {conv.partner?.pseudo?.[0]?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      {conv.is_online && <OnlineIndicator isOnline={true} className="absolute bottom-0 right-0" />}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-white truncate">
                          {conv.partner?.pseudo}
                        </span>
                        <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                          {conv.unread_count}
                        </span>
                      </div>
                      <p className="text-sm text-gray-400 truncate">
                        {conv.last_message}
                      </p>
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        )}

        {/* All Chat Conversations */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <MessageCircle className="w-4 h-4 text-gold" />
            <h2 className="text-sm font-medium text-white">
              Conversations récentes
            </h2>
          </div>
          
          {loading ? (
            <div className="text-center py-4 text-gray-500">Chargement...</div>
          ) : chatConversations.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Aucune conversation</p>
              <p className="text-sm mt-1">Cliquez sur un membre en ligne pour démarrer</p>
            </div>
          ) : (
            <div className="space-y-2">
              {chatConversations.map((conv) => (
                <Link
                  key={conv.partner?.id}
                  to={`/chat/${conv.partner?.id}`}
                  className="flex items-center gap-3 p-3 bg-dark-subtle rounded-lg hover:bg-dark-paper transition-colors"
                >
                  <div className="relative">
                    <Avatar className="w-12 h-12">
                      <AvatarImage src={getImageUrl(conv.partner?.profile_picture || conv.profile?.profile_picture)} />
                      <AvatarFallback className="bg-dark text-gold">
                        {conv.partner?.pseudo?.[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    {conv.is_online && <OnlineIndicator isOnline={true} className="absolute bottom-0 right-0" />}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-white truncate">
                        {conv.partner?.pseudo}
                      </span>
                      {conv.unread_count > 0 && (
                        <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                          {conv.unread_count}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-400 truncate">
                      {conv.last_message || "Nouvelle conversation"}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
